import { CSSProperties, useEffect, useState } from 'react';
import { EntityCard } from '@mb.io/core-components';
import Box from '@mui/material/Box';
import { generatePath, useNavigate } from 'react-router-dom';
import Pagination from '@mui/material/Pagination';
import { makeStyles } from '@mui/styles';
import { BASE_ROUTE } from '../../constants';
import { EntityCardContent } from './EntityCardContent';

const useStyles = makeStyles(() => ({
  paginationContainer: {
    marginTop: '2rem',
    alignSelf: 'end',
  },
  entityCardsViewContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  itemCardGrid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gridAutoRows: 'minmax(200px, auto)',
    [`@media (max-width: 840px)`]: {
      gridTemplateColumns: 'repeat(1, 1fr)',
    },
    [`@media (min-width: 840px) and (max-width: 1210px)`]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [`@media (min-width: 1210px) and (max-width: 1490px)`]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
    [`@media (min-width: 1490px)`]: {
      gridTemplateColumns: 'repeat(4, 1fr)',
    },
  },
}));

const cardStyles: CSSProperties = {
  height: '320px',
  margin: 0,
};

export type EntityCardsViewProps = {
  cardsPerPage: number;
  totalItems: number;
  page: number;
  formattedItems: any[];
  handleChangePage: (newPage: number) => void;
};

export const EntityCardsView = ({
  cardsPerPage,
  totalItems,
  page,
  formattedItems,
  handleChangePage,
}: Readonly<EntityCardsViewProps>) => {
  const navigate = useNavigate();
  const classes = useStyles();

  const totalNumberOfPages = Math.ceil(totalItems / cardsPerPage);

  const [pageNumber, setPageNumber] = useState(1);

  useEffect(() => {
    setPageNumber(page + 1); // This is done so that the page number starts from 0
  }, [page]);

  const handleNavigate = (entity: any) => {
    const path = generatePath(`${BASE_ROUTE}/:namespace/:kind/:name`, {
      namespace: entity.namespace ?? 'default',
      kind: entity.kind,
      name: entity.name,
    });
    navigate(path);
  };

  return (
    <Box className={classes.entityCardsViewContainer}>
      {formattedItems && (
        <Box className={classes.itemCardGrid}>
          {formattedItems?.map((item: any, index: number) => {
            return (
              <EntityCard
                key={`${item.kind}-${item.namespace}-${item.name}-index`}
                content={<EntityCardContent item={item} index={index} />}
                action={() => handleNavigate(item)}
                customStyles={cardStyles}
              />
            );
          })}
        </Box>
      )}
      <Pagination
        data-testid="entity-cards-view-pagination"
        className={classes.paginationContainer}
        count={totalNumberOfPages}
        page={pageNumber}
        onChange={(_, currentPageNumber) => {
          // Because the page number starts from 0
          handleChangePage(currentPageNumber - 1);
        }}
        color="primary"
      />
    </Box>
  );
};
